import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { api } from '../api.ts';

/**
 * Live updates for Messages. One WebSocket per signed-in screen, opened with a one-minute pass from
 * the API. Each update names the conversation that changed, and only that data is fetched again:
 * no page reload, no flicker. If the connection drops, it reconnects with a growing wait, and the
 * screens fall back to checking every few seconds until it is back.
 */

export type LiveState = 'off' | 'connecting' | 'live' | 'retrying';

let state: LiveState = 'off';
const listeners = new Set<() => void>();
function setState(next: LiveState) {
  if (state === next) return;
  state = next;
  for (const l of listeners) l();
}

export function useInboxLiveState(): LiveState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => state,
  );
}

export function useInboxLive(enabled: boolean): void {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    let ws: WebSocket | null = null;
    let stopped = false;
    let attempt = 0;
    let ping: ReturnType<typeof setInterval> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;

    const refresh = (conversationId?: string) => {
      void qc.invalidateQueries({ queryKey: ['messages-unread'] });
      void qc.invalidateQueries({ queryKey: ['messages'] });
      if (conversationId)
        void qc.invalidateQueries({ queryKey: ['message-thread', conversationId] });
      else void qc.invalidateQueries({ queryKey: ['message-thread'] });
    };

    const schedule = () => {
      if (stopped) return;
      setState('retrying');
      attempt += 1;
      clearTimeout(retry);
      retry = setTimeout(() => void connect(), Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5)));
    };

    async function connect() {
      if (stopped) return;
      setState(attempt === 0 ? 'connecting' : 'retrying');
      let url: string | null;
      try {
        url = (await api.post<{ url: string | null }>('/admin/messages/live-ticket')).data.url;
      } catch {
        schedule();
        return;
      }
      if (stopped) return;
      if (!url) {
        setState('off');
        return;
      }
      const socket = new WebSocket(url);
      ws = socket;
      socket.onopen = () => {
        attempt = 0;
        setState('live');
        clearInterval(ping);
        ping = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) socket.send('ping');
        }, 25_000);
        // Anything that changed while the connection was down.
        refresh();
      };
      socket.onmessage = (e) => {
        if (e.data === 'pong') return;
        try {
          const event = JSON.parse(String(e.data)) as { conversation_id?: string };
          refresh(event.conversation_id);
        } catch {
          refresh();
        }
      };
      socket.onclose = () => {
        clearInterval(ping);
        if (ws === socket) schedule();
      };
    }

    // Phones drop connections in the background; come back at once when the screen is shown again.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        clearTimeout(retry);
        attempt = 0;
        void connect();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    void connect();
    return () => {
      stopped = true;
      clearTimeout(retry);
      clearInterval(ping);
      document.removeEventListener('visibilitychange', onVisible);
      const s = ws;
      ws = null;
      s?.close();
      setState('off');
    };
  }, [enabled, qc]);
}
